import type { SearchResponse } from '@algolia/client-search';

import type { AppConfig } from '#/config/types/AppConfig';
import type { ApiUnpublishedEvent } from '#/modules/listing/events/api/types/ApiUnpublishedEvent';
import type { ApiUnpublishedInternship } from '#/modules/listing/internships/api/types/ApiUnpublishedInternship';
import type { ApiUnpublishedVolop } from '#/modules/listing/volops/api/types/ApiUnpublishedVolop';
import { searchAlgoliaOptionsLocationData } from '#/modules/search/algolia/options/searchAlgoliaOptionsLocationData';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import { searchAlgoliaIndex } from '#/modules/search/algolia/searchAlgoliaIndex';
import { searchAlgoliaVolopsIndex } from '#/modules/search/algolia/searchAlgoliaVolopsIndex';
import type { SearchAlgoliaRecommendedListingHit } from '#/modules/search/algolia/types/SearchAlgoliaRecommendedListingHit';
import { algoliaSearch } from '#/utils/algolia/algoliaSearch';

export function searchAlgoliaFetchRecommendedListings(
  unpublishedListing:
    | ApiUnpublishedInternship
    | ApiUnpublishedVolop
    | ApiUnpublishedEvent,
  appConfig: AppConfig,
): Promise<SearchResponse<SearchAlgoliaRecommendedListingHit>> {
  const { address, remoteOptions } = unpublishedListing;

  const locationData =
    remoteOptions.locationType === 'ONSITE'
      ? searchAlgoliaOptionsLocationData('', {
          latitude: address.latitude,
          longitude: address.longitude,
        })
      : undefined;

  const locationFilters = locationData?.locationFilters ?? [
    `(locationType:'${remoteOptions.locationType}')`,
  ];

  const typeFilters = [];
  if (unpublishedListing.type === 'UNPUBLISHED_INTERNSHIP')
    typeFilters.push(["type:'INTERNSHIP'"]);
  if (unpublishedListing.type === 'UNPUBLISHED_EVENT')
    typeFilters.push(["type:'EVENT'"]);

  return algoliaSearch<SearchAlgoliaRecommendedListingHit>({
    config: getSearchAlgoliaDefaultConfig({ appConfig }),
    query: '',
    indexName:
      unpublishedListing.type === 'UNPUBLISHED_VOLOP'
        ? searchAlgoliaVolopsIndex({ sort: 'newest', appConfig })
        : searchAlgoliaIndex({ sort: 'newest', appConfig }),
    options: {
      filters: [...locationFilters, ...typeFilters].join(' AND '),
      ...locationData?.locationOptions,
      hitsPerPage: 8,
    },
    isUsingAlgoliaMock: appConfig.algolia.mock,
  });
}
